function upperFirst(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

type Mutation = 'edit' | 'create' | 'upsert'
export type Action = Mutation | 'delete' | 'get'
export type Entity =
    | 'action'
    | 'activity dependency'
    | 'activity template'
    | 'ai chat'
    | 'ai chat prompt'
    | 'ai chat file'
    | 'workflow'
    | 'workflow version'
    | 'workflow feedback'
    | 'calendar event'
    | 'campaign'
    | 'credit balance'
    | 'procedure'
    | 'country'
    | 'external_event'
    | 'fileMeta'
    | 'focus list'
    | 'form question'
    | 'form'
    | 'kpis'
    | 'linked entity'
    | 'location list'
    | 'location ownership'
    | 'location'
    | 'process flow'
    | 'report'
    | 'smart data'
    | 'team'
    | 'unit list'
    | 'unit'
    | 'user'
    | 'invitation'
    | 'reason code'
    | 'fix'
    | 'unit type'
    | 'report status'
    | 'report status flow'
    | 'report comment'
    | 'post'
    | 'post comment'
    | 'procedure collaborator'
    | 'schedule'
    | 'integration job'
    | 'action'
    | 'report answer'
    | 'unit version'
    | 'guide'
    | 'notification subscription'
    | 'urlReference'
    | 'reportGuide'
    | 'metric definition'

export const DomainErrors = {
    unexpectedServerError: `Unexpected error`,
    unexpectedError: (action: Action, entity: Entity) => `Unexpected error: unable to ${action} ${entity}`,

    doesNotExist: (entity: Entity) => `${upperFirst(entity)} does not exist`,
    alreadyExists: (entity: Entity) => `${upperFirst(entity)} already exists`,
    notACollaborator: `User is not a collaborator`,
    bothOrNeither: (a: Entity, b: Entity) => `Must provide both ${a} & ${b} or neither`,

    /**
     * Id can be a string, a number, an array of either of those or really anything that gives extra information
     * to identify the entity(ies) on which this happened
     */
    unauthorized: (a: Action, e: Entity, id: any = '') => `Insufficient rights to ${a} ${e} ${id}`.trimEnd(),
    unauthorizedOrMissing: (a: Action, e: Entity) => `${upperFirst(e)} does not exist or user is not allowed to ${a}`,

    notNull: (table: Entity, columnName: string) => `Column ${columnName} of table ${table} cannot be null`,
    hasToBeUnique: (...relations: Entity[]) => `(${relations.join(', ')}) has to be unique`,
    hasToBePositive: (field: string) => `${field} cannot be negative`,
    circularReference: 'Circular reference not allowed',

    invalid: (item: string) => `Invalid ${item}`,
    invalidColumns: (columns: string[]) => `Invalid columns ${columns.join(', ')}`,
    invalidDateRange: `Start date is greater than end date`,

    activity: {
        cannotChangeTemplate: `Cannot assign an existing activity to a different template`,
    },

    calendar: {
        noEventsProvided: 'No events provided',
    },

    campaign: {
        updateOptionsNotSpecified: 'Name, start date, or end date required for updating campaign',
        invalidWorkflow: 'Workflow must be assigned to campaign',
    },

    flow: {
        triggersRequireFlow: 'Flow triggers can only be on flow workflows',
        duplicateStatusAction: 'Can only set one update-status action per trigger',
    },

    form: {
        question: {
            noFields: 'Must provide fields to update',
            needDataSources: 'Need to provide data sources for an option question.',
            invalidDataSourceIds: 'Some provided data source IDs do not exist.',
        },
        needQuestions: 'Must provide questions to order',
        incompleteQuestions: 'Missing some questions for the form in the data provided',
        tooManyQuestions: 'Too many questions have been passed for ordering',
        wrongQuestions: 'The question ids do not match the questions for the form in the database',
    },

    guides: {
        tags: 'incorrect `tags` format',
        invalidGuideIds: 'Guide ids does not exist',
    },

    invites: {
        noSSODetails: 'No SSO details for user',
    },

    location: {
        noLocations: 'No locations are currently configured for this hive',
        forbiddenForeignIds: 'The foreign ids provided have disallowed key names',
        integrationIdAlreadyExists: 'The integration ID provided already exists',
    },

    report: {
        needsCampaign: 'Report needs campaign',
        needsDefaultStatus: 'Report needs default status',
        oneDefaultStatus: 'Report must have one and only one default status',
        cannotSetStatus: 'Cannot set status to report',
        cannotSetTitle: 'Cannot set title for report',
        alreadyComplete: 'Report is already complete',
        hasBeenCancelled: 'Report has been cancelled',
        notReportCollaborator: 'You are not a collaborator on this report',
        reportIsIncomplete: 'Report is incomplete',
        reportIsInvalid: 'Report validation failed',
        reportIsNotOpen: 'Report is not open',
        invalidSignature: 'Invalid signature',
        invalidSignatureTimestamp: 'Invalid signature timestamp',
        noSignatureProvided: 'Signature required but none provided',
    },

    reportApi: {
        invalidSubmittedDateFilter: 'Submitted after must be before submitted before',
        invalidFromUntilFilter: 'From must be before until',
        invalidClientIds: 'reportClientIds must be valid UUIDs',
        noConfidentialReportPermission: 'You do not have permission for confidential reports',
    },

    team: {
        noDeleteParent: 'Cannot delete team with children',
    },

    timeline: {
        needsCampaign: 'Timeline save needs user to be attached to a campaign',
    },

    post: {
        deletionConsistency: '"lastModificationType" does not match "isDeleted" boolean',
        dateStateConsistency: 'Dates are not consistent with "lastModificationType"',
        dateValidity: '"createdAt" and "lastModifiedAt" must be in the past',
        targetValidity: 'Post must target a "unit", a "location" or a "procedure"',
        outdated: "Update was rejected because it's too old or on a deleted post",
    },
    schedule: {
        tooManyUnitsSelectedLimit: 'SubjectUnitIds for a schedule must be shorter than 100',
    },
    security: {
        notLoggedIn: '[ERR 000001] User is not logged in',
        notAdmin: '[100001] User is not an admin',
        notAllowedToAccessModule: '[100003] User is not allowed to access this module',
        notAllowedToRecommend: '[100004] User is not allowed to recommend people',
        notBusinessManager: '[100005] User is not a business manager',
        notUserManager: '[100006] User is not a user manager',
        notDataManager: '[100007] User is not a data manager',
    },
    files: {
        alreadyComplete: 'File is already complete',
        invalidStatus: 'Invalid status for file type',
    },
    metric: {
        nameMustBeUnique: 'Metric name must be unique',
    },
}

export default DomainErrors
